<template>
  <component
    :is="clickable ? 'button' : 'div'"
    class="card"
    :class="{ cardSpan2: spanCols === '2', cardClickable: clickable }"
    :type="clickable ? 'button' : undefined"
    @click="onClick"
  >
    <header class="cardHead">
      <span class="cardTitle">{{ title }}</span>
      <span v-if="versionLabel" class="cardVersion">{{ versionLabel }}</span>
      <span v-if="badge" class="cardBadge" :class="badgeToneClass">
        <AppIcon v-if="badgeTone === 'built'" class="cardBadgeIcon" name="checkmark" :size="11" />
        {{ badge }}
      </span>
    </header>
    <div class="cardBody">
      <slot />
    </div>
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AppIcon from '../../components/AppIcon.vue'

const props = withDefaults(defineProps<{
  title: string
  badge?: string
  badgeTone?: 'built' | 'empty'
  versionLabel?: string
  spanCols?: '1' | '2'
  clickable?: boolean
}>(), {
  badgeTone: 'built',
  clickable: true,
})

const emit = defineEmits<{
  (e: 'click'): void
}>()
const badgeToneClass = computed(() =>
  props.badgeTone === 'empty' ? 'cardBadgeEmpty' : 'cardBadgeBuilt',
)

function onClick() {
  emit('click')
}
</script>

<style scoped>
.card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-4);
  background: var(--color-bg-app);
  border: 0;
  border-radius: var(--radius-lg);
  text-align: left;
  cursor: default;
  box-shadow: var(--shadow-neu-out-cream-sm);
  transition:
    box-shadow var(--duration-base) var(--ease-out),
    transform var(--duration-fast) var(--ease-premium);
  color: var(--color-text-primary);
  font: inherit;
}
.cardClickable { cursor: pointer; }
.cardClickable:hover { box-shadow: var(--shadow-neu-out-cream); }
.cardClickable:active { transform: scale(0.997); }

.cardSpan2 { grid-column: span 2; }

.cardHead {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}
.cardTitle {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-wide);
  line-height: var(--lh-tight);
  text-transform: uppercase;
  color: var(--color-text-secondary);
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cardVersion {
  font-family: var(--font-mono);
  font-size: var(--fs-xs);
  color: var(--color-text-tertiary);
}
.cardBadge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px var(--space-2);
  border-radius: var(--radius-full);
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-tight);
  line-height: 1;
}
.cardBadgeBuilt {
  background: var(--color-accent-soft);
  color: var(--color-accent);
}
.cardBadgeEmpty {
  background: var(--color-surface-subtle, #f3f4f6);
  color: var(--color-text-tertiary);
}
.cardBadgeIcon { color: var(--color-accent); }

.cardBody {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
</style>
